/**
 * SEO utilities — meta tags, JSON-LD structured data, canonical URLs
 */

const BASE_URL = 'https://help.whitelabelclaw.com';

interface MetaTagsOptions {
  title: string;
  description: string;
  url: string;
  type?: string;
  image?: string;
}

export function setMetaTags(opts: MetaTagsOptions): void {
  document.title = opts.title;

  const setMeta = (name: string, content: string, attr = 'name') => {
    let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.content = content;
  };

  setMeta('description', opts.description);
  setMeta('og:title', opts.title, 'property');
  setMeta('og:description', opts.description, 'property');
  setMeta('og:url', opts.url, 'property');
  setMeta('og:type', opts.type || 'website', 'property');
  if (opts.image) {
    setMeta('og:image', opts.image, 'property');
  }
  setMeta('twitter:card', 'summary');
  setMeta('twitter:title', opts.title);
  setMeta('twitter:description', opts.description);
}

export function clearMetaTags(): void {
  const names = ['description', 'twitter:card', 'twitter:title', 'twitter:description'];
  const props = ['og:title', 'og:description', 'og:url', 'og:type', 'og:image'];
  for (const n of names) {
    document.querySelector(`meta[name="${n}"]`)?.remove();
  }
  for (const p of props) {
    document.querySelector(`meta[property="${p}"]`)?.remove();
  }
}

export function setCanonical(url: string): void {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
}

export function clearCanonical(): void {
  document.querySelector('link[rel="canonical"]')?.remove();
}

export function setJsonLd(data: object | object[]): void {
  clearJsonLd();
  const items = Array.isArray(data) ? data : [data];
  for (const item of items) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  }
}

export function clearJsonLd(): void {
  document.querySelectorAll('script[data-seo="true"]').forEach((el) => el.remove());
}

export function buildCanonicalUrl(locale: string, path?: string): string {
  return path ? `${BASE_URL}/${locale}/${path}` : `${BASE_URL}/${locale}`;
}

export function buildBreadcrumbLd(items: { name: string; url: string }[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
